import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { spacing, fontStyles, colors } from '../../styles';
import * as storage from '../../storage';

import Page from '../Page';
import Header from '../Header';
import PortfoliosSection from './PortfoliosSection';

const VALID_TIME_RANGES = ['1d', '1w', '1m', '3m', 'ytd', '1y', '5y'] as const;
type PortfolioTimeRange = (typeof VALID_TIME_RANGES)[number];

const isValidTimeRange = (value: string | null): value is PortfolioTimeRange => {
  return value !== null && VALID_TIME_RANGES.includes(value as PortfolioTimeRange);
};

const PortfoliosList: React.FC = () => {
  const { t } = useTranslation();
  const [portfolios, setPortfolios] = useState<storage.Portfolio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInterval, setSelectedInterval] = useState<PortfolioTimeRange>(() => {
    try {
      const saved = localStorage.getItem('portfolioTimeRange');
      return isValidTimeRange(saved) ? saved : '1w';
    } catch (e) {
      console.warn('Failed to load time range preference:', e);
      return '1w';
    }
  });
  const history = useHistory();

  // Function to load portfolios
  const loadPortfolios = useCallback(async () => {
    setIsLoading(true);
    try {
      const loadedPortfolios = await storage.getPortfolios();
      setPortfolios(loadedPortfolios);
    } catch (error) {
      console.error('Error loading portfolios:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load portfolios on mount
  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  // Refresh portfolios when this component comes into focus
  useEffect(() => {
    const handleFocus = () => {
      loadPortfolios();
    };

    window.addEventListener('focus', handleFocus);

    // Also register a history listener to detect when user navigates back to this page
    const unlisten = history.listen((location) => {
      if (location.pathname === '/portfolios/compare') {
        loadPortfolios();
      }
    });

    return () => {
      window.removeEventListener('focus', handleFocus);
      unlisten();
    };
  }, [history, loadPortfolios]);

  const pageStyles = css`
    padding: ${spacing.milli} 0px;
    padding-bottom: calc(${spacing.milli} + ${spacing.giga});
    max-width: 100%;
    overflow-x: hidden;

    @media (max-width: 359px) {
      padding: ${spacing.micro} 0px;
    }
  `;

  const topStyle = css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 0 0 ${spacing.milli} 0;
  `;

  const loadingStyles = css`
    text-align: center;
    padding: ${spacing.giga} ${spacing.deci};
    color: var(--secondary-text);
    ${fontStyles.dog};
  `;

  const timeRangeSelectStyles = css`
    display: inline-flex;
    align-items: center;
    padding: ${spacing.micro} ${spacing.centi};
    border-radius: 6px;
    border: 1px solid var(--border-color);
    background-color: var(--card-background);
    color: var(--text-color);
    ${fontStyles.mouse};
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    height: 30px;
    background-image: url('data:image/svg+xml;utf8,<svg fill="%23${(colors.liquorice || '000000').substring(
      1,
    )}" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/><path d="M0 0h24v24H0z" fill="none"/></svg>');
    background-repeat: no-repeat;
    background-position: right ${spacing.centi} center;
    padding-right: ${spacing.deka};

    &:hover {
      background-color: var(--hover-background);
    }
  `;

  return (
    <Page showNavigationBar>
      <Header headingText={t('portfolios.compare.title')} onBack={() => history.push('/portfolios')} />
      <div css={pageStyles}>
        {portfolios.length > 0 && (
          <div css={topStyle}>
            <select
              value={selectedInterval}
              onChange={(e) => {
                const val = e.target.value as PortfolioTimeRange;
                setSelectedInterval(val);
                try {
                  localStorage.setItem('portfolioTimeRange', val);
                } catch (err) {
                  console.warn('Failed to save time range preference:', err);
                }
              }}
              css={timeRangeSelectStyles}
              aria-label={t('portfolios.compare.selectTimeRange')}
            >
              <option value="1d">{t('portfolios.compare.1day')}</option>
              <option value="1w">{t('portfolios.compare.1week')}</option>
              <option value="1m">{t('portfolios.compare.1month')}</option>
              <option value="3m">{t('portfolios.compare.3months')}</option>
              <option value="ytd">{t('portfolios.compare.ytd')}</option>
              <option value="1y">{t('portfolios.compare.1year')}</option>
              <option value="5y">{t('portfolios.compare.5years')}</option>
            </select>
          </div>
        )}

        {isLoading ? (
          <div css={loadingStyles}>{t('portfolios.compare.loading')}</div>
        ) : (
          <PortfoliosSection portfolios={portfolios} selectedInterval={selectedInterval} />
        )}
      </div>
    </Page>
  );
};

export default PortfoliosList;
